import httpClient from '@common/api/httpClient';
import paramsSerializer from '@common/api/paramsSerializer';
import { maxSuggestionsReturned } from '@common/api/rest/helpers';
import {
  type AvailableFirmware,
  type FirmwareState,
  type FirmwareVersionType,
  type MassUpdateInstallationResponse,
  type MassUpdateResponse,
  type PaginatedMassUpdatesResponse,
  UserLevel,
} from '@common/types/api';
import { type InstallationId } from '@common/types/brandedTypes';
import { type FilterSuggestion } from '@common/types/deviceFilters';
import compareVersions from '@common/utils/device/compareVersions';
import { type ListWithCount } from '@common/utils/formatting/paginationQueryHelpers';

/**
 * @throws {ApiError} `conflict` when the firmware name is taken; the server's text is in
 * `serverMessage`. Other failures carry the server's text in `serverMessage` when it sent one.
 */
export async function createFirmware(
  name: string,
  file: File,
  comment?: string,
  retailerIds?: string[],
  minUserLevel?: UserLevel,
): Promise<void> {
  const formData = new FormData();
  formData.append('File', file);
  formData.append('Name', name);
  if (comment) {
    formData.append('Comment', comment);
  }
  retailerIds?.forEach((retailerId) => {
    formData.append('RetailerIds', retailerId);
  });
  if (minUserLevel && minUserLevel !== UserLevel.User) {
    formData.append('minUserLevel', String(minUserLevel));
  }

  await httpClient.post(
    '/rest/v1/Firmwares',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
}

/**
 * @throws {ApiError} `conflict` when the firmware name is taken; the server's text is in
 * `serverMessage`. Other failures carry the server's text in `serverMessage` when it sent one.
 */
export async function updateFirmware(
  id: string,
  name: string,
  comment?: string,
  retailerIds?: string[],
  minUserLevel?: UserLevel,
): Promise<void> {
  const formData = new FormData();
  formData.append('Name', name);
  if (comment) {
    formData.append('Comment', comment);
  }
  retailerIds?.forEach((retailerId) => {
    formData.append('retailerAccessIds', retailerId);
  });
  if (minUserLevel && minUserLevel !== UserLevel.User) {
    formData.append('minUserLevel', String(minUserLevel));
  }

  await httpClient.put(
    `/rest/v1/Firmwares/${id}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
}

export async function fetchFirmwareState(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<FirmwareState | undefined> {
  try {
    const { data } = await httpClient.get<FirmwareState>(
      `/rest/v1/Firmwares/installation/${installationId}/state`,
      { signal },
    );
    return data;
  } catch {
    return undefined;
  }
}

export async function fetchAllAvailableFirmwares(): Promise<AvailableFirmware[]> {
  const { data } = await httpClient.get<AvailableFirmware[]>('/rest/v1/Firmwares');
  return data.toSorted(
    (a, b) => b.createdUtc.localeCompare(a.createdUtc),
  );
}

export async function fetchAvailableFirmwares(
  installationId: InstallationId,
  signal: AbortSignal,
): Promise<AvailableFirmware[]> {
  try {
    const { data } = await httpClient.get<AvailableFirmware[]>(
      `/rest/v1/Firmwares/installation/${installationId}`,
      { signal },
    );
    return data;
  } catch {
    return [];
  }
}

export async function createPendingFirmware(
  installationId: InstallationId,
  firmwareId: string,
): Promise<void> {
  await httpClient.post(
    '/rest/v1/Firmwares/Updates',
    {
      installationId,
      firmwareId,
    },
  );
}

export async function deleteFirmware(firmwareId: string): Promise<void> {
  await httpClient.delete(
    `/rest/v1/Firmwares/${firmwareId}`,
  );
}

export async function fetchMassUpdates(
  skip: number,
  take: number,
): Promise<ListWithCount<MassUpdateResponse>> {
  const { data } = await httpClient.get<PaginatedMassUpdatesResponse>(
    '/rest/v1/Firmwares/MassUpdates',
    { params: { skip, take }, paramsSerializer },
  );
  return {
    count: data.count,
    items: data.massUpdates,
  };
}

export async function fetchMassUpdateDetails(id: string): Promise<MassUpdateInstallationResponse> {
  const { data } = await httpClient.get(`/rest/v1/Firmwares/MassUpdates/${id}`);
  return data;
}

export async function startMassUpdate(
  firmwareId: string,
  installationIds: Array<InstallationId>,
  name: string,
): Promise<string> {
  const { data: massUpdateId } = await httpClient.post(
    '/rest/v1/Firmwares/MassUpdates',
    {
      name,
      firmwareId,
      installationIds,
    },
  );
  return massUpdateId;
}

export async function fetchFirmwareVersions(
  filter: string,
  type: FirmwareVersionType,
): Promise<FilterSuggestion[]> {
  const { data } = await httpClient.get<string[]>(
    `/rest/v1/Firmwares/Versions/${type}`,
    {
      params: {
        filter,
        take: maxSuggestionsReturned,
      },
    },
  );
  return data.toSorted(compareVersions).map((version) => ({
    id: version,
    value: version,
  }));
}
